<template>
    <div class="accordion-panel">
        <div class="accordion-panel__header" :class="{'active': isActive}" @click="headlineClickHandler">
            Icon hinzufügen
        </div>

        <height-transition>
            <div class="accordion-panel__body" v-show="isActive">
                <div class="accordion-panel__body__inner">
                    <div class="panel-section-headline has-tooltip" @click="showIconModal = true">
                        Icon auswählen
                    </div>
                    <div class="icons">
                        <div class="icon"
                             :class="{'selected': hasSelectedIcon && selectedIcon.getId() === icon.getId()}"
                             v-for="icon in icons"
                             :key="icon.getId()"
                             @click="iconSelectHandler(icon)"
                        >
                            <img v-if="icon.hasThumbnailUrl()" :src="icon.getThumbnailUrl()" :alt="icon.getName()">
                        </div>
                    </div>

                    <div class="panel-section-headline has-tooltip" @click="showColorModal = true">
                        Farbe auswählen
                    </div>
                    <color-selector :attribute="colorAttribute"
                                    :selected="selectedColor"
                                    @selected:color="colorSelectedHandler"
                    >
                    </color-selector>

                    <div class="panel-section-headline has-tooltip" @click="showScaleModal = true">
                        Größe auswählen
                    </div>
                    <scale-selector :scales="scales"
                                    :selected="selectedScale"
                                    v-if="hasScales"
                                    @selected:scale="scaleSelectedHandler"
                    ></scale-selector>
                </div>
            </div>
        </height-transition>

        <!-- Icon Modal -->
        <ui-modal v-show="showIconModal" @close:clicked="showIconModal = false">
            <template v-slot:title>Icon auswählen</template>
            <template v-slot:content>
                <p>
                    Wir bieten Ihnen hier eine breite Palette verschiedenster Icons, welche Sie in Größe und Farbe
                    anpassen können.
                </p>
            </template>
        </ui-modal>

        <!-- Color Modal -->
        <ui-modal v-show="showColorModal" @close:clicked="showColorModal = false">
            <template v-slot:title>Icon - Farbauswahl</template>
            <template v-slot:content>
                <p>
                    Wählen Sie eine Farbe für das ausgewählte Icon über die vorgegebene Farbpalette aus.
                </p>
            </template>
        </ui-modal>

        <!-- Scale Modal -->
        <ui-modal v-show="showScaleModal" @close:clicked="showScaleModal = false">
            <template v-slot:title>Icon - Skalierung</template>
            <template v-slot:content>
                <p>
                    Bitte legen Sie hier die Größe für das ausgewählte Icon fest.
                </p>
            </template>
        </ui-modal>
    </div>
</template>

<script>
    import AttributeTree               from "../../../expivi/attributes/AttributeTree"
    import AttributeMaterialGroupValue from "../../../expivi/attributes/AttributeMaterialGroupValue"
    import AttributeValue              from "../../../expivi/attributes/AttributeValue"
    import Attribute                   from "../../../expivi/attributes/Attribute"

    export default {
        name:     "IconPanel",
        created:  function () {
            this.loadIcons()

            // Watch changes to the selected material (icon) id and update the store accordingly
            this.$watch(function () {
                return this.iconMaterialNode.data.selected_value_value.material_id;
            }, function (newMaterial) {
                let icon = this.icons.filter(icon => icon.getId() === newMaterial);

                if (icon.length > 0) {
                    this.updateIcon(icon[0]);
                } else {
                    this.updateIcon(null);
                }
            })

            // Reset icon customization on create in case it had previously been set
            this.resetCustomization()
        },
        props:    {
            customization:  {
                required: true,
                type:     AttributeValue
            },
            position:       {
                required: true,
                type:     AttributeTree
            },
            colorAttribute: {
                required: true,
                type:     Attribute
            },
            scales:         {
                required: true,
                type:     Array,
                default:  function () {
                    return []
                }
            }
        },
        data:     function () {
            return {
                icons:            [],
                iconAttributeId:  null,
                iconMaterialNode: null,
                showIconModal:    false,
                showColorModal:   false,
                showScaleModal:   false
            }
        },
        computed: {
            /**
             * Returns the slug of the position. Used for dynamic store handling.
             */
            positionSlug: function () {
                return this.position.getRoot().getSlug()
            },

            /**
             * Returns the selected color as defined in the position's store.
             */
            selectedColor: function () {
                return this.$store.state[this.positionSlug].color
            },

            /**
             * Returns the selected scale as defined in the position's store
             * @returns {*}
             */
            selectedScale: function () {
                return this.$store.state[this.positionSlug].scale
            },

            /**
             * Returns the selected icon as defined in the position's store
             */
            selectedIcon: function () {
                return this.$store.state[this.positionSlug].icon
            },

            hasSelectedIcon: function () {
                return this.callStoreGetter('hasIcon')
            },

            /**
             * Verify if we have selectable scales
             * @returns {boolean}
             */
            hasScales: function () {
                return this.scales.length > 0
            },

            isActive: function () {
                return this.callStoreGetter('hasIconCustomization')
            }
        },
        methods:  {
            /**
             * Call a store getter
             */
            callStoreGetter(getterName) {
                let slug = this.positionSlug
                return this.$store.getters[slug + '/' + getterName]
            },

            /**
             * Call a store mutation
             */
            callStoreMutation(mutationName, payload) {
                let slug = this.positionSlug
                return this.$store.commit(slug + '/' + mutationName, payload)
            },

            /**
             * Loads the available icons from the position
             *
             * TODO Default icon
             */
            loadIcons: function () {
                let node              = this.position.filterByType('mytw_conf_position_material')
                this.iconMaterialNode = node

                if (node.hasAttributeValues()) {
                    let attributeValue   = node.getAttributeValues()[0]
                    this.iconAttributeId = attributeValue.getAttributeId()

                    if (attributeValue instanceof AttributeMaterialGroupValue) {
                        // Filter the icons from the materials - which are all visible
                        let materials = attributeValue.getValuableMaterials()
                        this.icons    = materials.filter(material => material.isVisible())

                        // Set the default icon
                        this.updateIcon(this.icons[0])
                    }
                }
            },

            /**
             * Maps the updateCustomization mutation to the dynamic store module
             *
             * @param customization
             */
            updateCustomization: function (customization) {
                this.callStoreMutation('updateCustomization', customization)
            },

            /**
             * Resets the customization to null value.
             */
            resetCustomization: function() {
                this.updateCustomization(null);
            },

            /**
             * Maps the updateIcon mutation of the dynamic store module
             *
             * @param icon
             */
            updateIcon: function (icon) {
                this.callStoreMutation('updateIcon', icon)
            },

            /**
             * Pass the selected icon to the parent component
             *
             * @param icon
             */
            iconSelectHandler: function (icon) {
                this.$expiviReady.then(expivi => {
                    expivi.setProductAttribute(this.iconAttributeId, icon.getId())
                })
            },

            /**
             * Pass the selected color to the parent component
             */
            colorSelectedHandler: function (color) {
                this.$emit('selected:color', color)
            },

            /*+
             * Pass the selected scale event to the parent component
             */
            scaleSelectedHandler: function (scale) {
                this.$emit('selected:scale', scale)
            },

            /**
             * Handles the click on the headline and thus the enabling of the icon customizationType.
             */
            headlineClickHandler: function () {
                if (this.isActive === false) {
                    this.$expiviReady.then(expivi => {
                        let customizationOptionsNode = this.position.filterByType('mytw_conf_position_customization')

                        if (!this.isActive) {
                            this.updateCustomization(this.customization)
                            expivi.setProductAttribute(customizationOptionsNode.getId(), this.customization.getId())
                        }
                    })

                    this.$emit('on:active')
                }
            }
        }
    }
</script>

<style scoped>

</style>
